import { useEffect, useRef, useState, type RefObject } from 'react';

/** How long the "Copied" confirmation stays up. */
export const COPIED_FLASH_MS = 2400;

/** Marks a log row, so a highlighted event counts as one line however many cells it spans. */
export const LOG_LINE_ATTR = 'data-log-line';

export interface CopyNotice {
  /** Log lines copied; for a shell selection, its non-empty lines. */
  lines: number;
}

const ranges = (selection: Selection) =>
  Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index));

const textLines = (text: string) => text.split(/\r?\n/).filter((line) => line.trim()).length;

/**
 * Copies whatever the pointer highlights inside `rootRef` to the clipboard when the button is
 * released, as if the reader had pressed Copy. `readExtra` supplies a selection the browser
 * cannot see, such as xterm's own. Returns what was just copied while the confirmation shows.
 */
export function useCopyOnSelect(
  rootRef: RefObject<HTMLElement | null>,
  readExtra?: () => string,
): CopyNotice | null {
  const [notice, setNotice] = useState<CopyNotice | null>(null);
  const readExtraRef = useRef(readExtra);
  const lastRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    readExtraRef.current = readExtra;
  });

  useEffect(() => {
    const onMouseUp = (event: MouseEvent) => {
      const root = rootRef.current;
      if (!root) return;
      // Let the browser finish updating the selection before reading it.
      setTimeout(() => {
        const selection = window.getSelection();
        const inside =
          selection !== null &&
          !selection.isCollapsed &&
          (root.contains(selection.anchorNode) || root.contains(selection.focusNode));
        const rows = inside
          ? [...root.querySelectorAll(`[${LOG_LINE_ATTR}]`)].filter((row) =>
              ranges(selection).some((range) => range.intersectsNode(row)),
            ).length
          : 0;
        const text = inside
          ? selection.toString()
          : event.target instanceof Node && root.contains(event.target)
            ? (readExtraRef.current?.() ?? '')
            : '';
        if (!text.trim()) {
          lastRef.current = '';
          return;
        }
        // One drag fires once; re-selecting the same words later copies again.
        if (text === lastRef.current) return;
        lastRef.current = text;
        navigator.clipboard
          ?.writeText(text)
          .then(() => {
            setNotice({ lines: rows || textLines(text) });
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setNotice(null), COPIED_FLASH_MS);
          })
          // Blocked or unsupported: leave the selection as it is for a manual copy.
          .catch(() => {
            lastRef.current = '';
          });
      }, 0);
    };
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [rootRef]);

  return notice;
}
